import { LoadingStateType, LoadingStatuses } from '@/common/constants';
import { CourseCloneStatusCard } from '@/components/courses/CourseCloneStatusCard';
import React, { useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const Stack = styled.div`
  position: fixed;
  bottom: var(--default-padding);
  display: flex;
  flex-direction: column;
  gap: var(--small-padding);
  z-index: 10000;
`;

export type CourseCloneJobStatus = LoadingStateType;

export type CourseCloneJobResult = {
  clonedCourseId: string;
  targetAccountId: string;
  targetAccountName?: string;
  isOtherAccount: boolean;
};

export type CourseCloneJob = {
  id: string;
  status: CourseCloneJobStatus;
  progress?: number;
  result?: CourseCloneJobResult;
};

type CourseCloneStatusStackProps = {
  jobs: CourseCloneJob[];
  onRemove: (jobId: string) => void;
};

const STACK_GAP_PX = 12;

export const CourseCloneStatusStack: React.FC<CourseCloneStatusStackProps> = ({
  jobs,
  onRemove,
}) => {
  const { t } = useTranslation();
  const [left, setLeft] = useState(0);

  useLayoutEffect(() => {
    const navEl = document.querySelector('.navigation') as HTMLElement | null;
    if (!navEl) return;

    const update = () =>
      setLeft((navEl.getBoundingClientRect().width || 0) + STACK_GAP_PX);

    update();

    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;

    observer?.observe(navEl);
    window.addEventListener('resize', update);

    return () => {
      window.removeEventListener('resize', update);
      observer?.disconnect();
    };
  }, []);

  if (jobs.length === 0) return null;

  return (
    <Stack style={{ left }}>
      {jobs.map(({ id, status, progress, result }) => {
        if (status === LoadingStatuses.LOADING) {
          return (
            <CourseCloneStatusCard
              key={id}
              variant="loading"
              jobId={id}
              title={t('content.clone.status.cloning')}
              detail={
                progress !== undefined
                  ? t('content.clone.status.progress', { progress })
                  : undefined
              }
            />
          );
        }

        if (!result) return null;

        return (
          <CourseCloneStatusCard
            key={id}
            variant="success"
            jobId={id}
            title={t('content.clone.status.success.title')}
            detail={
              result.isOtherAccount && result.targetAccountName
                ? t('content.clone.status.success.detail.other', {
                    accountName: result.targetAccountName,
                  })
                : t('content.clone.status.success.detail.same')
            }
            clonedCourseId={result.clonedCourseId}
            targetAccountId={result.targetAccountId}
            isOtherAccount={result.isOtherAccount}
            onRemove={onRemove}
          />
        );
      })}
    </Stack>
  );
};
